/**
 * Tiny framework-free helpers shared across the attendance surfaces (widget,
 * day table, month sheet, correction dialog). Kept dependency-light so both the
 * client components and the dialog can reuse the same time/duration formatting.
 */

/**
 * Extract an 'HH:mm' clock time from a server timestamp. Accepts the contract's
 * 'YYYY-MM-DD HH:mm:ss' clinic-local form, an ISO 'YYYY-MM-DDTHH:mm:ss', or an
 * already-trimmed 'HH:mm[:ss]'. Returns '' for null/blank so callers fall back
 * to the em-dash empty value themselves.
 */
export function toHHmm(value: string | null | undefined): string {
  if (!value) return ""
  // The date portion (YYYY-MM-DD) carries no colon, so the first HH:mm match is
  // always the clock time regardless of the separator.
  const match = /(\d{2}):(\d{2})/.exec(value)
  return match ? `${match[1]}:${match[2]}` : ""
}

/**
 * Format whole minutes as a compact 'Xh Ym' duration ('2h 15m', '45m', '3h').
 * Returns null for null/undefined so callers can render the em-dash empty value.
 * Negative inputs clamp to 0.
 */
export function formatWorkedMinutes(
  minutes: number | null | undefined,
): string | null {
  if (minutes == null) return null
  const total = Math.max(0, Math.round(minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}
